import { useEffect, useRef, useState } from 'react';
import type { Recurrence } from '../types/agenda';
import { extractOrdinal, toRFC3339, weekdayOf } from '../util/recurrence';
import './recurrence-modal.css';

const SCHEME_OPTIONS = [
  { value: 'weekly', label: 'Every meeting (weekly)' },
  { value: 'biweekly', label: 'Every other week' },
  { value: 'meeting-ordinal', label: 'Nth weekday of month' },
  { value: 'monthly', label: 'Same date each month' },
  { value: 'daily', label: 'Every day' },
  { value: 'yearly', label: 'Same date each year' },
  { value: 'manual', label: 'Manual (include per meeting)' },
];

const WEEKDAY_SHORT = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const WEEKDAY_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const ORDINAL_OPTIONS = [
  { value: 1, label: '1st' },
  { value: 2, label: '2nd' },
  { value: 3, label: '3rd' },
  { value: 4, label: '4th' },
  { value: 5, label: '5th' },
];
const ORDINAL_LABELS = ['', '1st', '2nd', '3rd', '4th', '5th'];

// Go serializes time.Time zero value as "0001-01-01T00:00:00Z" — treat as unset.
function toDateInput(dateStr: string | undefined): string {
  if (!dateStr) return '';
  const s = dateStr.slice(0, 10);
  return s < '1900-01-01' ? '' : s;
}

// Returns YYYY-MM-DD for the Nth occurrence of `weekday` in the month of `refDate`.
function nthWeekdayOfMonth(refDate: string | undefined, weekday: number, ordinal: number): string {
  const today = new Date();
  const year = refDate ? parseInt(refDate.slice(0, 4), 10) : today.getFullYear();
  const month = refDate ? parseInt(refDate.slice(5, 7), 10) - 1 : today.getMonth();
  let count = 0;
  for (let d = 1; d <= 31; d++) {
    const dt = new Date(year, month, d);
    if (dt.getMonth() !== month) break;
    if (dt.getDay() === weekday) {
      count++;
      if (count === ordinal) {
        return `${String(year).padStart(4, '0')}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      }
    }
  }
  // No Nth occurrence this month (e.g. no 5th) — fall back to the previous one.
  return nthWeekdayOfMonth(refDate, weekday, ordinal - 1);
}

export function RecurrenceModal({
  open,
  recurrence,
  agendaDate,
  onSave,
  onClose,
}: {
  open: boolean;
  recurrence: Recurrence | undefined;
  agendaDate?: string;
  onSave: (rec: Recurrence) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  const [scheme, setScheme] = useState('weekly');
  const [ordinal, setOrdinal] = useState(1);
  const [weekday, setWeekday] = useState(0);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    if (!open) return;
    const s = recurrence?.scheme && recurrence.scheme !== 'week-of-month' ? recurrence.scheme : 'weekly';
    setScheme(s);
    setOrdinal(extractOrdinal(recurrence?.pattern) || 1);
    setWeekday(weekdayOf(recurrence?.startDate ?? agendaDate));
    setStartDate(toDateInput(recurrence?.startDate) || toDateInput(agendaDate) || '');
    setEndDate(toDateInput(recurrence?.endDate));
  }, [open]);

  useEffect(() => {
    const dlg = ref.current;
    if (!dlg) return;
    if (open && !dlg.open) dlg.showModal();
    else if (!open && dlg.open) dlg.close();
  }, [open]);

  const handleSave = () => {
    let effectiveStart = startDate;
    let pattern: string | undefined;

    if (scheme === 'meeting-ordinal') {
      effectiveStart = nthWeekdayOfMonth(startDate || agendaDate, weekday, ordinal);
      pattern = `BYSETPOS=${ordinal}`;
    }

    const rec: Recurrence = {
      scheme,
      ...(effectiveStart ? { startDate: toRFC3339(effectiveStart) } : {}),
      ...(endDate ? { endDate: toRFC3339(endDate) } : {}),
      ...(pattern ? { pattern } : {}),
    };

    onSave(rec);
    onClose();
  };

  const onBackdropClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === ref.current) onClose();
  };

  const previewLabel =
    scheme === 'meeting-ordinal'
      ? `${ORDINAL_LABELS[ordinal] ?? `${ordinal}th`} ${WEEKDAY_FULL[weekday]} of each month`
      : null;

  return (
    <dialog ref={ref} className="recurrence-modal" onClose={onClose} onClick={onBackdropClick}>
      <div className="recurrence-modal-panel">
        <div className="recurrence-modal-header">
          <h2 className="recurrence-modal-title">Custom recurrence</h2>
          <button className="recurrence-modal-close" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="recurrence-modal-body">
          <label className="field">
            <span className="field-sublabel">Repeats</span>
            <select className="input" value={scheme} onChange={(e) => setScheme(e.target.value)}>
              {SCHEME_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>

          {scheme === 'meeting-ordinal' && (
            <>
              <div className="field">
                <span className="field-sublabel">Which occurrence</span>
                <div className="rec-pills">
                  {ORDINAL_OPTIONS.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      className={`rec-pill${ordinal === value ? ' active' : ''}`}
                      onClick={() => setOrdinal(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="field">
                <span className="field-sublabel">Day of week</span>
                <div className="rec-pills">
                  {WEEKDAY_SHORT.map((label, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className={`rec-pill${weekday === idx ? ' active' : ''}`}
                      onClick={() => setWeekday(idx)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {previewLabel && <p className="recurrence-preview">{previewLabel}</p>}
            </>
          )}

          <div className="field-row">
            <label className="field">
              <span className="field-sublabel">Start date</span>
              <input
                type="date"
                className="input"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </label>
            <label className="field">
              <span className="field-sublabel">End date</span>
              <input
                type="date"
                className="input"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </label>
          </div>
        </div>

        <div className="recurrence-modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave}>
            Save
          </button>
        </div>
      </div>
    </dialog>
  );
}
